import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Phone, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { SITE_LOGO_SRC } from "@/lib/site";
import { cn } from "@/lib/utils";

const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Projects", to: "/projects" },
  { label: "About", to: "/about" },
  { label: "Testimonials", to: "/testimonials" },
  { label: "Staff", to: "/staff" },
  { label: "Contact", to: "/contact" },
];

function BrandBlock({ isLight, className = "" }) {
  return (
    <div className={cn("flex items-center gap-3 min-w-0", className)}>
      <div
        className={cn(
          "shrink-0 flex size-11 sm:size-12 items-center justify-center rounded-full overflow-hidden",
          "bg-[#0A0A0A] border border-[#C5A880]/35 shadow-sm",
        )}
      >
        <img
          src={SITE_LOGO_SRC}
          alt=""
          width={48}
          height={48}
          className="h-[125%] w-[125%] max-w-none object-contain object-center scale-[0.72]"
          decoding="async"
        />
      </div>
      <div className={cn("min-w-0 flex flex-col leading-tight text-left", isLight ? "text-white" : "text-[#111111]")}>
        <span
          className="font-serif text-base sm:text-lg font-semibold tracking-tight truncate text-[#D4AF37] drop-shadow-[0_2px_0_rgba(0,0,0,0.22)]"
        >
          HOMERAJ
        </span>
        <span className="text-[10px] sm:text-xs font-light uppercase tracking-[0.18em] text-[#C5A880]">
          REALTY
        </span>
      </div>
    </div>
  );
}

export default function Navbar({ transparent = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleScheduleVisit = () => {
    setOpen(false);
    navigate("/contact");
  };

  const isLight = transparent && !scrolled;

  const linkClass = ({ isActive }) =>
    cn(
      "text-sm font-light tracking-wide transition-colors duration-200 hover:text-[#C5A880]",
      isLight
        ? isActive
          ? "text-[#C5A880]"
          : "text-white/90"
        : isActive
          ? "text-[#C5A880]"
          : "text-[#4A4A4A]",
    );

  const mobileLinkClass = ({ isActive }) =>
    cn(
      "text-left text-base font-light py-1 rounded-lg px-1 -mx-1 transition-colors",
      isActive ? "text-[#C5A880]" : "text-[#4A4A4A] hover:text-[#C5A880]",
    );

  return (
    <nav
      data-testid="navbar"
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-500",
        scrolled || !transparent
          ? "bg-white/85 backdrop-blur-md md:backdrop-blur-lg shadow-sm border-b border-white/40"
          : "bg-transparent",
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 gap-3">
          <Link to="/" data-testid="navbar-logo" className="flex items-center min-w-0 shrink gap-2">
            <BrandBlock isLight={isLight} />
          </Link>

          <div className="hidden lg:flex items-center gap-7 shrink-0">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.label}
                to={link.to}
                end={link.to === "/"}
                data-testid={`navbar-link-${link.label.toLowerCase()}`}
                className={linkClass}
              >
                {link.label}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-3 sm:gap-4 shrink-0">
            <a
              href="tel:9310965215"
              data-testid="navbar-phone"
              className={cn(
                "hidden md:flex items-center gap-2 text-sm transition-colors duration-300 whitespace-nowrap",
                isLight ? "text-white/90" : "text-[#4A4A4A]",
              )}
            >
              <Phone className="w-4 h-4 shrink-0" />
              93109 65215
            </a>
            <Button
              data-testid="navbar-schedule-visit"
              onClick={() => navigate("/contact")}
              className="hidden md:flex bg-[#C5A880] hover:bg-[#8C6B43] text-white rounded-full px-6 h-10 font-light shrink-0"
            >
              Schedule Visit
            </Button>

            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  data-testid="navbar-mobile-menu"
                  aria-label="Open menu"
                  className={cn("lg:hidden p-2 -mr-2 rounded-lg transition-colors", isLight ? "text-white" : "text-[#111111]")}
                >
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[min(100vw-1rem,20rem)] bg-white border-l border-gray-100 flex flex-col">
                <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                <Link to="/" onClick={() => setOpen(false)} className="flex items-center gap-3 pb-4 border-b border-gray-100">
                  <BrandBlock isLight={false} />
                </Link>
                <nav className="mt-6 flex flex-col gap-1 flex-1">
                  {NAV_LINKS.map((link) => (
                    <NavLink
                      key={link.label}
                      to={link.to}
                      end={link.to === "/"}
                      onClick={() => setOpen(false)}
                      data-testid={`navbar-mobile-link-${link.label.toLowerCase()}`}
                      className={mobileLinkClass}
                    >
                      {link.label}
                    </NavLink>
                  ))}
                </nav>
                <div className="pt-6 mt-auto border-t border-gray-100 space-y-4">
                  <a href="tel:9310965215" className="flex items-center gap-2 text-[#4A4A4A] text-sm font-light">
                    <Phone className="w-4 h-4" /> 93109 65215
                  </a>
                  <Button onClick={handleScheduleVisit} className="w-full bg-[#C5A880] hover:bg-[#8C6B43] text-white rounded-full font-light">
                    Schedule Visit
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
